import { forwardRef } from 'react';
import { documentToReactComponents } from '@contentful/rich-text-react-renderer';
import { BLOCKS, INLINES, MARKS } from '@contentful/rich-text-types';
import { tildeToSoftHyphen } from '~utils/string';
import {
  renderBold,
  renderHeading,
  renderHr,
  renderHyperlink,
  renderItalic,
  renderListItem,
  renderOlList,
  renderParagraph,
  renderUlList,
} from './renderers';

import type { ElementType, ReactNode } from 'react';
import type { Options } from '@contentful/rich-text-react-renderer';
import type { Document } from '@contentful/rich-text-types';

export type RichTextRendererProps = {
  content: string;
  options?: Options;
  wrapperElement?: ElementType;
  className?: string;
};

export const RichTextRenderer = forwardRef<HTMLElement, RichTextRendererProps>(
  ({ content, options = {}, wrapperElement: WrapperElement, className }, ref) => {
    const { renderNode, renderMark, renderText } = options;
    const richTextDocument: Document = JSON.parse(tildeToSoftHyphen(content));
    const formattedContent: ReactNode = documentToReactComponents(richTextDocument, {
      renderNode: {
        [BLOCKS.HEADING_2]: renderHeading(2, 2),
        [BLOCKS.HEADING_3]: renderHeading(3, 3),
        [BLOCKS.HEADING_4]: renderHeading(4, 4),
        [BLOCKS.PARAGRAPH]: renderParagraph(),
        [BLOCKS.UL_LIST]: renderUlList(),
        [BLOCKS.OL_LIST]: renderOlList(),
        [BLOCKS.LIST_ITEM]: renderListItem(),
        [BLOCKS.HR]: renderHr(),
        [INLINES.HYPERLINK]: renderHyperlink(),
        ...renderNode,
      },
      renderMark: {
        [MARKS.BOLD]: renderBold(),
        [MARKS.ITALIC]: renderItalic(),
        ...renderMark,
      },
      renderText,
    });

    return WrapperElement ? (
      <WrapperElement ref={ref} className={className}>
        {formattedContent}
      </WrapperElement>
    ) : (
      formattedContent
    );
  },
);
